/**
 * SMS reunification compliance/readiness service (named "documents").
 *
 * Uses the typed `@tiaki/http-client` per ADR 113 §Decision. Two
 * readiness endpoints (school + district) are not in the placeholder
 * schema and fall through to `client.ky`.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

const REUNIFICATION_BASE = 'api/v2/reunification';

// ── Types ────────────────────────────────────────────────────────────────────

export type ReadinessStatus =
  | 'ready'
  | 'mostly_ready'
  | 'needs_attention'
  | 'not_ready';

export interface ComplianceItem {
  requirement: string;
  status: 'met' | 'not_met' | 'partial';
  details: string;
}

export interface ComplianceReport {
  compliance_standard: string;
  compliance_score: number;
  rating: 'excellent' | 'good' | 'acceptable' | 'needs_improvement' | 'critical';
  compliant_items: ComplianceItem[];
  non_compliant_items: ComplianceItem[];
  recommendations: string[];
  generated_at: string;
}

export interface SchoolReadiness {
  school_id: number;
  school_name: string;
  overall_score: number;
  plan_readiness: number;
  site_readiness: number;
  staff_readiness: number;
  communication_readiness: number;
  student_roster_completeness: number;
  equipment_readiness: number;
  recommendations: string[];
}

export interface DistrictReadiness {
  organization_id: number;
  organization_name: string;
  overall_score: number;
  overall_status: ReadinessStatus;
  total_schools: number;
  schools_ready: number;
  schools_not_ready: number;
  school_assessments: SchoolReadiness[];
}

export interface ExportOptions {
  format: 'csv' | 'xlsx' | 'json';
  include_students?: boolean;
  include_guardians?: boolean;
  include_staff?: boolean;
  include_audit_log?: boolean;
}

// ── API Functions ────────────────────────────────────────────────────────────

export async function getComplianceReport(
  eventPublicId: string,
): Promise<ComplianceReport> {
  const result = await client.GET(
    '/api/v2/reunification/events/{public_id}/compliance_report/',
    { params: { path: { public_id: eventPublicId } as never } },
  );
  return unwrap(result) as unknown as ComplianceReport;
}

export async function getSchoolReadiness(
  schoolId: number,
): Promise<SchoolReadiness> {
  // Not in the placeholder schema; falls through to ky.
  const response = await client.ky.get(
    `${REUNIFICATION_BASE}/readiness/school/?school_id=${schoolId}`,
  );
  return (await response.json()) as SchoolReadiness;
}

export async function getDistrictReadiness(): Promise<DistrictReadiness> {
  // Not in the placeholder schema; falls through to ky.
  const response = await client.ky.get(`${REUNIFICATION_BASE}/readiness/district/`);
  return (await response.json()) as DistrictReadiness;
}

export async function exportEventData(
  eventPublicId: string,
  options: ExportOptions,
): Promise<Blob> {
  // Blob response — use client.ky for binary handling.
  const response = await client.ky.post(
    `${REUNIFICATION_BASE}/events/${eventPublicId}/export_data/`,
    { json: options },
  );
  return response.blob();
}
